import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useApi';
import { useTenantContext } from '@/contexts/TenantContext';
import { apiClient } from '@/lib/api';
import { cognitoAuth } from '@/lib/cognito';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { RefreshCw } from 'lucide-react';

interface QueueData {
  location_id: string;
  service_id: string;
  waiting: Array<{
    name: string;
    phone: string;
    epoch: number;
    strikes: number;
    status: string;
    wait_minutes: number;
    party_size?: number;
  }>;
  called: Array<{
    name: string;
    phone: string;
    epoch: number;
    strikes: number;
    status: string;
    wait_minutes: number;
    party_size?: number;
  }>;
  being_serviced: Array<{
    name: string;
    phone: string;
    epoch: number;
    strikes: number;
    status: string;
    wait_minutes: number;
    party_size?: number;
  }>;
  no_show: Array<{
    name: string;
    phone: string;
    epoch: number;
    strikes: number;
    status: string;
    wait_minutes: number;
    party_size?: number;
  }>;
}

export function LiveDashboard() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const activeTenantId = selectedTenantId || user?.tenant_from_group;
  
  const [queues, setQueues] = useState<QueueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNoShow, setShowNoShow] = useState(false);
  const [showStrikes, setShowStrikes] = useState(false);
  const [showPartySize, setShowPartySize] = useState(false);
  const [refreshRate, setRefreshRate] = useState('300');
  const [customSeconds, setCustomSeconds] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const formatServiceLabel = (serviceId: string): string => {
    return serviceId
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const redactName = (name: string) => {
    if (!name || name.length < 2) return name || 'Anonymous';
    if (name.length <= 4) return name.charAt(0) + '***';
    return name.substring(0, 3) + '****' + name.charAt(name.length - 1);
  };

  const calculateAvgWait = (entries: any[]) => {
    if (!entries || entries.length === 0) return 0;
    const waitTimes = entries.map(e => e.wait_minutes || 0);
    return Math.round(waitTimes.reduce((sum, time) => sum + time, 0) / waitTimes.length);
  };

  const fetchDashboardData = useCallback(async () => {
    if (!activeTenantId) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    // Ensure token is set before API call
    const token = cognitoAuth.getIdToken();
    if (token) {
      apiClient.setToken(token);
    } else {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await apiClient.getDashboard(activeTenantId);
      setQueues(response.queues || []);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Dashboard refresh error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const getRefreshInterval = () => {
      if (refreshRate === 'custom' && customSeconds) {
        return parseInt(customSeconds) * 1000;
      }
      return parseInt(refreshRate) * 1000;
    };

    const interval = setInterval(fetchDashboardData, getRefreshInterval());
    return () => clearInterval(interval);
  }, [fetchDashboardData, refreshRate, customSeconds]);

  const renderCustomer = (customer: any, position?: number) => (
    <div key={customer.phone || Math.random()} className="flex justify-between items-center p-2 mb-1 bg-gray-50 rounded text-xs">
      <span className="font-mono font-semibold">
        {redactName(customer.name)}
        {showStrikes && customer.strikes > 0 && (
          <span className="text-red-600 text-xs ml-2 font-bold">{customer.strikes} strikes</span>
        )}
        {showPartySize && customer.party_size && customer.party_size > 0 && (
          <span className="text-purple-600 text-xs ml-2 font-bold">👥 {customer.party_size}</span>
        )}
      </span>
      <div className="flex items-center gap-2">
        {customer.wait_minutes !== undefined && (
          <span className="text-gray-600 text-xs">{customer.wait_minutes}m</span>
        )}
        {position !== undefined && (
          <span className="text-blue-600 font-bold text-xs">#{position}</span>
        )}
      </div>
    </div>
  );

  const renderQueueCard = (queue: QueueData) => {
    const totalInQueue = queue.waiting.length + queue.called.length + queue.being_serviced.length;
    const avgWait = calculateAvgWait(queue.waiting);

    return (
      <div key={`${queue.location_id}-${queue.service_id}`} className="bg-white rounded-lg p-3 shadow-md border overflow-hidden">
        <div className="border-b-2 border-blue-500 pb-2 mb-3">
          <h3 className="font-bold text-gray-800 text-sm leading-tight">{formatServiceLabel(queue.service_id)}</h3>
          <div className="flex gap-3 mt-1 text-xs text-gray-600 font-semibold">
            <span>In Queue: {totalInQueue}</span>
            <span>Avg Wait: {avgWait} min</span>
          </div>
        </div>

        <div className="space-y-2">
          <div>
            <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold mb-1">Being Serviced</div>
            {queue.being_serviced.length > 0 ? (
              queue.being_serviced.map(c => renderCustomer(c))
            ) : (
              <div className="text-gray-400 italic text-center py-2 text-xs">None</div>
            )}
          </div>

          <div>
            <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold mb-1">Called</div>
            {queue.called.length > 0 ? (
              queue.called.map(c => renderCustomer(c))
            ) : (
              <div className="text-gray-400 italic text-center py-2 text-xs">None</div>
            )}
          </div>

          <div>
            <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold mb-1">Waiting</div>
            {queue.waiting.length > 0 ? (
              queue.waiting.map((c, i) => renderCustomer(c, i + 1))
            ) : (
              <div className="text-gray-400 italic text-center py-2 text-xs">None</div>
            )}
          </div>

          {showNoShow && (
            <div>
              <div className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold mb-1">No Show</div>
              {queue.no_show.length > 0 ? (
                queue.no_show.map(c => renderCustomer(c))
              ) : (
                <div className="text-gray-400 italic text-center py-2 text-xs">None</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">Error loading dashboard: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 text-xs overflow-hidden">
      <div className="flex justify-between items-center mb-4 h-15">
        <h1 className="text-xl font-bold text-gray-800">{activeTenantId} - Queue Dashboard</h1>
        
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <Switch checked={showNoShow} onCheckedChange={setShowNoShow} />
            <label className="text-xs font-semibold text-gray-600">No-Show</label>
          </div>
          
          <div className="flex flex-col items-center gap-1">
            <Switch checked={showStrikes} onCheckedChange={setShowStrikes} />
            <label className="text-xs font-semibold text-gray-600">Strikes</label>
          </div>
          
          <div className="flex flex-col items-center gap-1">
            <Switch checked={showPartySize} onCheckedChange={setShowPartySize} />
            <label className="text-xs font-semibold text-gray-600">Party Size</label>
          </div>
          
          <div className="flex flex-col items-center gap-1">
            <label className="text-xs font-semibold text-gray-600">Refresh</label>
            <div className="flex items-center gap-2">
              <Select value={refreshRate} onValueChange={setRefreshRate}>
                <SelectTrigger className="w-16 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="60">1m</SelectItem>
                  <SelectItem value="120">2m</SelectItem>
                  <SelectItem value="300">5m</SelectItem>
                  <SelectItem value="600">10m</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              
              {refreshRate === 'custom' && (
                <Input
                  type="number"
                  placeholder="Sec"
                  min="10"
                  max="3600"
                  value={customSeconds}
                  onChange={(e) => setCustomSeconds(e.target.value)}
                  className="w-16 h-8 text-xs"
                />
              )}
              
              <Button onClick={fetchDashboardData} size="sm" className="h-8 px-3 text-xs">
                <RefreshCw className="h-3 w-3 mr-1" />
                Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 grid-rows-2 gap-4 h-[calc(100vh-120px)]">
        {queues.length > 0 ? (
          queues.map(renderQueueCard)
        ) : (
          <div className="col-span-4 row-span-2 flex items-center justify-center text-gray-400">
            No active queues found
          </div>
        )}
      </div>

      {lastUpdated && (
        <div className="fixed bottom-2 right-4 text-gray-500 text-xs">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}